/**
 * Maps Crowdflow / project field rows to react-awesome-query-builder field
 * definitions.
 * Resolution order: GraphQL `fieldType` -> merged knownFieldTypes (incl.
 * check-in defaults) -> text.
 */

export const TEXT_FIELD_OPERATORS = [
    'equal',
    'like',
    'not_equal',
    'ends_with',
    'is_empty',
    'is_not_empty',
];

/**
 * Fallback when API does not yet send `fieldType`.
 * Keys must match backend field names.
 */
export const DEFAULT_KNOWN_CHECKIN_FIELD_TYPES = {
    cf_checkInStatus: 'boolean',
    cf_checkInTime: 'datetime',
    cf_checkInDeployment: 'deployment',
};

function normalizeApiFieldType(fieldType) {
    if (fieldType == null || typeof fieldType !== 'string') return null;
    const t = fieldType.trim().toLowerCase();
    if (t === 'boolean' || t === 'bool') return 'boolean';
    if (t === 'datetime' || t === 'date_time' || t === 'datetimetz') {
        return 'datetime';
    }
    if (
        t === 'deployment' ||
        t === 'deployment_select' ||
        t === 'select_deployment' ||
        t === 'checked_in_deployment'
    ) {
        return 'deployment';
    }
    if (t === 'text' || t === 'string') return 'text';
    return null;
}

function resolveSemantic(row, mergedKnown) {
    const fromApi = normalizeApiFieldType(row.fieldType);
    if (fromApi && fromApi !== 'text') return fromApi;
    const fromMap = mergedKnown[row.field];
    if (
        fromMap === 'boolean' ||
        fromMap === 'datetime' ||
        fromMap === 'deployment'
    ) {
        return fromMap;
    }
    return 'text';
}

function buildFieldDef(row, semantic, deploymentList) {
    const { label } = row;

    if (semantic === 'boolean') {
        return {
            label,
            type: 'select',
            valueSources: ['value'],
            operators: ['equal'],
            fieldSettings: {
                listValues: [
                    { value: 'true', title: 'Yes' },
                    { value: 'false', title: 'No' },
                ],
            },
        };
    }

    if (semantic === 'datetime') {
        return {
            label,
            type: 'datetime',
            valueSources: ['value'],
        };
    }

    if (semantic === 'deployment') {
        return {
            label,
            type: 'select',
            valueSources: ['value'],
            operators: ['equal', 'not_equal'],
            fieldSettings: {
                listValues: (deploymentList || []).map((d) => ({
                    value: String(d.id),
                    title: d.name != null ? String(d.name) : String(d.id),
                })),
            },
        };
    }

    return {
        label,
        type: 'text',
        valueSources: ['value'],
        operators: TEXT_FIELD_OPERATORS,
    };
}

/**
 * @param {Array<{ field: string, label: string, fieldType: (string|undefined) }>}
 * fieldRows
 * @param {object} options
 * @param {Array<{ id: (string|number), name: (string|undefined) }>} [options.deploymentList]
 * @param {Record<string, 'boolean'|'datetime'|'deployment'>}
 * [options.knownFieldTypes]
 * @returns {Record<string, object>}
 */
export function buildQueryBuilderFieldsConfig(fieldRows, options = {}) {
    const { deploymentList = [], knownFieldTypes = {} } = options;
    const mergedKnown = {
        ...DEFAULT_KNOWN_CHECKIN_FIELD_TYPES,
        ...knownFieldTypes,
    };

    if (!Array.isArray(fieldRows)) {
        return {};
    }

    return fieldRows.reduce((acc, row) => {
        if (!row?.field || row.label == null || row.label === '') {
            return acc;
        }
        const semantic = resolveSemantic(row, mergedKnown);
        acc[row.field] = buildFieldDef(row, semantic, deploymentList);
        return acc;
    }, {});
}
